import React from 'react';
import 'BRAND_THEME_IMPORT_PATH';
import 'BRAND_FONTS_IMPORT_PATH';
import '@cs-cms/ui/styles';
import Root from './Root';
import { dayjsSetup } from '@cs-cms/utils';
import {
  CurrentUserProvider,
  DocumentFormProvider,
  DocumentsProvider,
  CpsLoaderProvider,
  GlobalizationProvider,
  MsGraphProvider,
  NotificationContextProvider,
  QueryProviderWrapper,
  SearchProvider,
  ServicesProvider,
  UiAdminConfigsProvider,
  ViewsProvider
} from '@cs-cms/state-management';
import { BrowserGuard, CpsNotification, UIProvider } from '@cs-cms/ui';
import { ApplicationInsightsProvider } from './providers/ApplicationInsightsProvider';
import { appConfig } from '@shared/config/appConfig';
import Logo from '@shared/assets/images/Logo.svg?react';
import { ThemeProvider, useTheme } from '@shared/lib/theme/ThemeContext';
import { globalizationSetup } from '@shared/config/globalization/globalizationSetup';

const AppWithUI: React.FC = () => {
  const { theme, toggleTheme } = useTheme();

  return (
    <UIProvider
      config={{
        ...appConfig,
        theme,
        toggleTheme,
        Logo
      }}
    >
      <DocumentFormProvider>
        <NotificationContextProvider
          renderToast={({ show, type, message, timeout }, onClose) => (
            <CpsNotification show={show} type={type} message={message} timeout={timeout} onClose={onClose} />
          )}
        >
          <QueryProviderWrapper appInsights={appConfig.appInsights} graphSharesEndpoint={appConfig.graphSharesEndpoint}>
            <GlobalizationProvider>
              <div className="app">
                <CurrentUserProvider>
                  <CpsLoaderProvider>
                    <MsGraphProvider>
                      <SearchProvider>
                        <ViewsProvider>
                          <DocumentsProvider apiUrl={appConfig.apiUrl} zipName={appConfig.zipName}>
                            <UiAdminConfigsProvider>
                              <BrowserGuard>
                                <Root />
                              </BrowserGuard>
                            </UiAdminConfigsProvider>
                          </DocumentsProvider>
                        </ViewsProvider>
                      </SearchProvider>
                    </MsGraphProvider>
                  </CpsLoaderProvider>
                </CurrentUserProvider>
              </div>
            </GlobalizationProvider>
          </QueryProviderWrapper>
        </NotificationContextProvider>
      </DocumentFormProvider>
    </UIProvider>
  );
};

const App: React.FC = () => {
  React.useEffect(() => {
    dayjsSetup();
    globalizationSetup();
  }, []);

  return (
    <ApplicationInsightsProvider>
      <ServicesProvider>
        <ThemeProvider>
          <AppWithUI />
        </ThemeProvider>
      </ServicesProvider>
    </ApplicationInsightsProvider>
  );
};

export default App;
